import { CHART_SECTIONS, CHART_TABS } from '../components/nodes/nodeChartLayout';
import { CHART_TYPES, SECTIONS } from '../components/nodes/nodeChartSpec';
import {
  DEFAULT_COLUMNS,
  flowPlace,
  layoutBottom,
  scaleWidth
} from '../components/layout/gridLayout';
import { APP_TABS } from './navigation';
import { getPanel } from './panelRegistry';

/**
 * Раскладки вкладок по умолчанию и описание элементов сетки.
 *
 * Умолчание воспроизводит вид вкладки до появления сетки (1280–1535 px): у тенанта без
 * сохранённой раскладки ничего не должно сдвинуться. Координаты — в 12 столбцах; для сетки
 * в 24 столбца они масштабируются.
 */

/**
 * Вид элемента решает, что в нём растягивается.
 * - `fill` — график, высота задаёт высоту области графика;
 * - `auto` — столбчатый график, высота которого растёт по числу строк; заданная — минимум;
 * - `flow` — таблица, шапка, фильтры: высота по содержимому, правится только ширина и место;
 * - `heading` — заголовок группы графиков.
 */
export const ITEM_KINDS = {
  Fill: 'fill',
  Auto: 'auto',
  Flow: 'flow',
  Heading: 'heading'
};

/**
 * Минимальная ширина по виду, в 12 столбцах. У таблиц и шапок она большая: внутри них
 * брейкпоинты по ширине окна, а не элемента, и в узкой ячейке их вёрстка ломается.
 */
const MIN_WIDTH = { fill: 3, auto: 4, flow: 6, heading: 3 };

/** Вкладки с сеткой. Остальные разделы сверстаны стопкой и раскладке не подлежат. */
export const LAYOUT_TABS = ['dashboard', 'employees', 'call-paths'];

export const layoutTabTitle = (tab) => APP_TABS.find((entry) => entry.id === tab)?.label ?? tab;

/** Статус раскладки вкладки для экрана настроек. Время — в поясе браузера, как в журнале. */
export const describeLayoutStatus = (layout) => {
  if (!layout) {
    return 'по умолчанию';
  }

  const savedAt = layout.updatedAtUtc ? new Date(layout.updatedAtUtc) : null;
  if (!savedAt || Number.isNaN(savedAt.getTime())) {
    return 'своя';
  }

  const label = savedAt.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
  return `своя, сохранена ${label}`;
};

/**
 * Секции графиков нод живут на вкладках по `CHART_TABS`, и там вкладка «Общей статистики»
 * исторически называется `general`, а раздел приложения — `dashboard`.
 */
const NODE_CHART_TAB = { dashboard: 'general', employees: 'employees' };

export const nodeChartSections = (tab) =>
  CHART_TABS.find((entry) => entry.id === NODE_CHART_TAB[tab])?.sections ?? [];

export const nodeItemId = (chartId) => `node:${chartId}`;

export const isNodeItemId = (id) => id.startsWith('node:');

export const nodeHeadingId = (tab, sectionId) => `ui:${tab}.nodes-${sectionId}`;

const STATIC_LAYOUTS = {
  dashboard: [
    { id: 'ui:dashboard.call-dynamics', x: 0, y: 0, w: 12, h: 9, kind: ITEM_KINDS.Fill },
    { id: 'ui:dashboard.calls-by-employee', x: 0, y: 9, w: 12, h: 8, kind: ITEM_KINDS.Auto },
    { id: 'ui:dashboard.node-filters', x: 0, y: 17, w: 12, h: 2, kind: ITEM_KINDS.Flow }
  ],
  // До сетки четыре графика нагрузки на мониторах от 1536 px стояли в ряд, ниже — по два.
  // Раскладка одна на все ширины от 1280 px, поэтому по умолчанию — по два, как на ноутбуках.
  employees: [
    { id: 'ui:employees.header', x: 0, y: 0, w: 12, h: 5, kind: ITEM_KINDS.Flow },
    { id: 'ui:employees.kpi-strip', x: 0, y: 5, w: 12, h: 3, kind: ITEM_KINDS.Flow },
    { id: 'ui:employees.operator-efficiency', x: 0, y: 8, w: 12, h: 8, kind: ITEM_KINDS.Flow },
    { id: 'ui:employees.quality-comparison', x: 0, y: 16, w: 12, h: 9, kind: ITEM_KINDS.Auto },
    { id: 'ui:employees.quality-trend', x: 0, y: 25, w: 6, h: 9, kind: ITEM_KINDS.Fill },
    { id: 'ui:employees.calls-trend', x: 6, y: 25, w: 6, h: 9, kind: ITEM_KINDS.Fill },
    { id: 'ui:employees.weekday-workload', x: 0, y: 34, w: 6, h: 9, kind: ITEM_KINDS.Fill },
    { id: 'ui:employees.hourly-workload', x: 6, y: 34, w: 6, h: 9, kind: ITEM_KINDS.Fill },
    { id: 'ui:employees.hourly-by-day', x: 0, y: 43, w: 6, h: 9, kind: ITEM_KINDS.Fill },
    { id: 'ui:employees.wait-hold-hourly', x: 6, y: 43, w: 6, h: 9, kind: ITEM_KINDS.Fill },
    { id: 'ui:employees.forbidden-phrases', x: 0, y: 52, w: 12, h: 8, kind: ITEM_KINDS.Auto }
  ],
  'call-paths': [
    { id: 'ui:call-paths.header', x: 0, y: 0, w: 12, h: 2, kind: ITEM_KINDS.Flow },
    { id: 'ui:call-paths.overview', x: 0, y: 2, w: 12, h: 10, kind: ITEM_KINDS.Flow },
    // Внутри разбора своя сетка из карточек с брейкпоинтами по окну: уже он не читается.
    { id: 'ui:call-paths.drilldown', x: 0, y: 12, w: 12, h: 4, kind: ITEM_KINDS.Flow, minW: 12 }
  ]
};

/**
 * Элементы сетки, которые рисуют одну из нескольких панелей реестра (слот) или несколько сразу
 * (составной элемент). Панели внутри по-прежнему переименовываются и скрываются в «Интерфейсе»;
 * место на сетке у них общее.
 */
export const LAYOUT_SLOTS = {
  'ui:call-paths.overview': ['ui:call-paths.types', 'ui:call-paths.outcomes'],
  'ui:call-paths.drilldown': [
    'ui:call-paths.pair-trend',
    'ui:call-paths.employees',
    'ui:call-paths.quality',
    'ui:call-paths.calls'
  ]
};

/** Панели реестра, у которых на вкладке есть место: свои элементы и панели внутри слотов. */
export const coveredPanelIds = (tab) =>
  new Set(staticLayout(tab).flatMap(({ id }) => [id, ...(LAYOUT_SLOTS[id] ?? [])]));

/**
 * Элементы сетки, которых нет в реестре панелей: их нельзя ни переименовать, ни скрыть в
 * «Интерфейсе». Заголовки секций в реестр не заведены намеренно — e2e ищет их по тексту, а
 * текст из реестра тенант переименовывает.
 */
export const SYNTHETIC_ITEMS = {
  'ui:dashboard.node-filters': { title: 'Фильтры по результатам анализа' },
  'ui:employees.kpi-strip': { title: 'Плитки показателей' },
  'ui:call-paths.overview': { title: 'Обзор типов или итогов' },
  'ui:call-paths.drilldown': { title: 'Разбор выбранного типа или итога' },
  ...Object.fromEntries(
    Object.keys(NODE_CHART_TAB).flatMap((tab) =>
      nodeChartSections(tab).map((sectionId) => [
        nodeHeadingId(tab, sectionId),
        {
          title: `Заголовок «${CHART_SECTIONS.find((section) => section.id === sectionId)?.title ?? sectionId}»`
        }
      ])
    )
  )
};

export const staticLayout = (tab) => STATIC_LAYOUTS[tab] ?? [];

/** Вид статического элемента; у карточек нод вид приходит вместе с карточкой. */
export const staticItemKind = (tab, id) => {
  if (id.startsWith(`ui:${tab}.nodes-`)) {
    return ITEM_KINDS.Heading;
  }

  return STATIC_LAYOUTS[tab]?.find((item) => item.id === id)?.kind ?? ITEM_KINDS.Flow;
};

export const minWidthFor = (kind, cols = DEFAULT_COLUMNS) =>
  scaleWidth(MIN_WIDTH[kind] ?? MIN_WIDTH.flow, cols);

/** Минимальная ширина элемента: своя у статического элемента, если задана, иначе — по виду. */
export const itemMinWidth = (tab, id, kind, cols = DEFAULT_COLUMNS) => {
  const own = STATIC_LAYOUTS[tab]?.find((item) => item.id === id)?.minW;
  return own ? Math.min(scaleWidth(own, cols), cols) : minWidthFor(kind, cols);
};

/** Название элемента в редакторе: подпись тенанта для панели, описание — для синтетики. */
export const layoutItemTitle = (id, panels) =>
  SYNTHETIC_ITEMS[id]?.title ?? (getPanel(id) ? panels.title(id) : id);

/**
 * Скрыть из редактора можно то, что скрывается и в «Интерфейсе»: обычную панель реестра.
 * Шапку с фильтрами раздела (`renameOnly`) нельзя — вместе с ней пропали бы фильтры.
 */
export const canHidePanel = (id) => {
  const panel = getPanel(id);
  return Boolean(panel) && panel.renameOnly !== true && panel.group !== 'kpi' && panel.group !== 'column';
};

/**
 * Размер карточки нод по умолчанию. Повторяет сегодняшнюю сетку секций: распределения — по
 * половине ширины, широкие (`lg`, `full`) — во всю; чек-листы и цитаты — всегда во всю.
 */
export const nodeItemSize = (spec, sectionId) => {
  // Высота по умолчанию — минимум, и он не должен превышать содержимое: иначе карточка без
  // сохранённой раскладки стала бы выше, чем до сетки. Чек-лист на два пункта — 183 px.
  if (sectionId !== SECTIONS.Distribution) {
    return { w: DEFAULT_COLUMNS, h: 4, kind: ITEM_KINDS.Flow };
  }

  const wide = spec?.size === 'lg' || spec?.size === 'full';
  const heights = { sm: 9, md: 11, lg: 12, full: 12 };
  return {
    w: wide ? DEFAULT_COLUMNS : DEFAULT_COLUMNS / 2,
    h: heights[spec?.size] ?? heights.md,
    kind: spec?.type === CHART_TYPES.Bar ? ITEM_KINDS.Auto : ITEM_KINDS.Fill
  };
};

/**
 * Раскладка вкладки по умолчанию: статические элементы, затем по каждой секции графиков нод —
 * заголовок и карточки потоком. Скрытые настройкой карточки встают в самый низ: в поток видимых
 * их ставить нельзя, иначе после их удаления и уплотнения половинные карточки переехали бы
 * на другую сторону.
 *
 * `nodeSections` — `[{ id, items: [{ id, w, h }] }]` видимых карточек в порядке каталога;
 * `hiddenNodeItems` — `[{ id, w, h }]`.
 */
export const buildTabDefaults = (tab, { nodeSections = [], hiddenNodeItems = [] } = {}) => {
  const placed = staticLayout(tab).map(({ id, x, y, w, h }) => ({ id, x, y, w, h }));

  for (const section of nodeSections) {
    if (section.items.length === 0) {
      continue;
    }

    const headingY = layoutBottom(placed);
    placed.push({ id: nodeHeadingId(tab, section.id), x: 0, y: headingY, w: DEFAULT_COLUMNS, h: 1 });
    placed.push(...flowPlace(section.items, { startY: headingY + 1 }));
  }

  placed.push(...flowPlace(hiddenNodeItems, { startY: layoutBottom(placed) }));
  return placed;
};
